import { useState, useEffect } from 'react';
import { notificationsApi } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Bell, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function NotificationsPage() {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadNotifications();
    }, []);

    const loadNotifications = async () => {
        try {
            const response = await notificationsApi.getAll();
            setNotifications(response.data);
        } catch (error) {
            console.error('Error loading notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAllRead = async () => {
        const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
        if (unreadIds.length === 0) return;
        
        try {
            await notificationsApi.markRead(unreadIds);
            loadNotifications();
            toast.success('Notifiche segnate come lette');
        } catch (error) {
            console.error('Error:', error);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-heading text-xl font-semibold">Notifiche</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        {unreadCount > 0 ? `${unreadCount} notifiche non lette` : 'Nessuna notifica non letta'}
                    </p>
                </div>
                {unreadCount > 0 && (
                    <Button variant="outline" onClick={markAllRead}>
                        <Check className="w-4 h-4 mr-2" />
                        Segna tutte come lette
                    </Button>
                )}
            </div>

            <Card>
                <CardContent className="p-0">
                    {notifications.length === 0 ? (
                        <div className="py-12 text-center">
                            <Bell className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                            <p className="text-muted-foreground">Nessuna notifica</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {notifications.map((notification) => (
                                <div 
                                    key={notification.id}
                                    className={`p-4 ${!notification.read ? 'bg-accent/5' : ''}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`w-2 h-2 rounded-full mt-2 ${!notification.read ? 'bg-accent' : 'bg-muted'}`} />
                                        <div className="flex-1">
                                            <p className={`text-sm ${!notification.read ? 'font-medium' : ''}`}>
                                                {notification.message || notification.title}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {formatDateTime(notification.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
